import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineXL } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { Dialog } from '@sorare/core/src/components/dialog';
import { glossary } from '@sorare/core/src/lib/glossary';

import type { LoyaltyBoxDialog_probabilisticBundle } from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  padding: var(--double-unit);
`;

type Props = {
  loyaltyBox: LoyaltyBoxDialog_probabilisticBundle;
  onClose: () => void;
  open: boolean;
};
export const LoyaltyBoxDialog = ({ loyaltyBox, onClose, open }: Props) => {
  return (
    <Dialog open={open} maxWidth="xs" fullScreen={false} onClose={onClose}>
      <Wrapper center gap={2} className="text-center">
        <ResponsiveImg
          src={loyaltyBox.config.pictureUrl}
          cropWidth={160}
          width="160"
          alt=""
        />

        <HeadlineXL as="h1">
          <FormattedMessage
            id="LoyaltyBoxButton.Title"
            defaultMessage="We've gifted you a {br}<strong>{loyaltyBoxName}</strong>"
            values={{
              loyaltyBoxName: loyaltyBox.config.name,
              strong: Bold,
              br: <br />,
            }}
          />
        </HeadlineXL>
        <BodyM>
          <FormattedMessage
            id="LoyaltyBoxButton.Description"
            defaultMessage="It's our way of saying thank you for your loyalty. Open it at the start of the new season"
          />
        </BodyM>
        {loyaltyBox.openableAt !== null ? (
          <Countdown date={loyaltyBox.openableAt} />
        ) : null}

        <Button fullWidth size="medium" color="primary" onClick={onClose}>
          <FormattedMessage {...glossary.gotIt} />
        </Button>
      </Wrapper>
    </Dialog>
  );
};

LoyaltyBoxDialog.fragments = {
  probabilisticBundle: gql`
    fragment LoyaltyBoxDialog_probabilisticBundle on ProbabilisticBundle {
      id

      openableAt
      config {
        name
        id
        pictureUrl
      }
    }
  ` as TypedDocumentNode<LoyaltyBoxDialog_probabilisticBundle>,
};
